import { css, Rule } from 'glamor'
import * as React from 'react'
import moment from 'moment'
import * as PropTypes from 'prop-types'
import { injectIntl, FormattedMessage, defineMessages, FormattedDate, WrappedComponentProps } from 'react-intl'

import { IWithThemeProps, withTheme } from '../../theme'

import { Button, ButtonVariant, ButtonSize } from '../button'
import { Flex } from '../flex'
import { Popup } from '../popup'

import { DaterangeDropdownRanges, predefinedRangeShape } from './daterange-dropdown-ranges'
import { DaterangePicker } from './daterange-picker'
import { IDatepickerPredefinedRange } from './types'

const messages = defineMessages({
  ok: {
    id: 'ui-kit.daterange-dropdown.ok',
    defaultMessage: 'OK',
  },
  clear: {
    id: 'ui-kit.daterange-dropdown.clear',
    defaultMessage: 'Clear',
  },
  reset: {
    id: 'ui-kit.daterange-dropdown.reset',
    defaultMessage: 'Reset',
  },
  createdAtButton: {
    id: 'ui-kit.daterange-dropdown.any-date',
    defaultMessage: 'Any date',
  },
  rangeFromInfinity: {
    id: 'ui-kit.daterange-dropdown.from-infinity',
    defaultMessage: 'The dawn of time',
  },
  rangeToInfinity: {
    id: 'ui-kit.daterange-dropdown.to-infinity',
    defaultMessage: 'Now',
  },
})

interface IDaterangeDropdownProps {
  values?: (moment.Moment | null)[]
  onChange: (values: (moment.Moment | null | undefined)[]) => void
  onClear?: () => void
  onReset?: () => void
  onDone?: () => void
  showPredefinedRanges?: boolean
  predefinedRanges?: IDatepickerPredefinedRange[]
  firstSelectableDate?: moment.Moment
  lastSelectableDate?: moment.Moment
  style?: Rule
  buttonVariant?: ButtonVariant
  buttonSize?: ButtonSize
}

export const DaterangeDropdown = withTheme(
  injectIntl(
    class DaterangeDropdown extends React.Component<IDaterangeDropdownProps & WrappedComponentProps & IWithThemeProps> {
      static propTypes = {
        values: PropTypes.array,
        onChange: PropTypes.func.isRequired,
        onClear: PropTypes.func,
        onReset: PropTypes.func,
        onDone: PropTypes.func,
        showPredefinedRanges: PropTypes.bool,
        predefinedRanges: PropTypes.arrayOf(predefinedRangeShape) as any,
        firstSelectableDate: PropTypes.object as any,
        lastSelectableDate: PropTypes.object as any,
        style: PropTypes.object,
        intl: PropTypes.object as any,
        buttonVariant: PropTypes.oneOf(Button.VARIANTS),
        buttonSize: PropTypes.oneOf(Button.SIZES),
        children: PropTypes.node,
      }

      onPeriodChange = ({ toDate, fromDate }: { toDate?: moment.Moment | null; fromDate?: moment.Moment | null }) => {
        this.props.onChange([fromDate, toDate])
      }

      onDone = (cb: () => void) => {
        this.props.onDone && this.props.onDone()
        cb()
      }

      getLabel() {
        const { values = [], children } = this.props

        const [from, to] = values

        if (from && to)
          return (
            <span>
              <FormattedDate value={moment(from).toDate()} />
              {' – '}
              <FormattedDate value={moment(to).toDate()} />
            </span>
          )
        if (from)
          return (
            <span>
              <FormattedDate value={moment(from).toDate()} />
              {' – '}
              <FormattedMessage {...messages.rangeToInfinity} />
            </span>
          )
        if (to)
          return (
            <span>
              <FormattedMessage {...messages.rangeFromInfinity} />
              {' – '}
              <FormattedDate value={moment(to).toDate()} />
            </span>
          )
        if (children) return children

        return <FormattedMessage {...messages.createdAtButton} />
      }

      static BUTTON_VARIANTS = Button.VARIANTS

      render() {
        const {
          values = [],
          firstSelectableDate,
          lastSelectableDate,
          showPredefinedRanges,
          onClear,
          onReset,
          predefinedRanges,
          style,
          buttonVariant = 'clear',
          buttonSize,
          theme,
        } = this.props

        return (
          <div {...css({ display: 'inline-flex' }, style)}>
            <Popup
              renderPopup={({ toggleOpen }) => (
                <Flex row>
                  <Flex>
                    <DaterangePicker
                      firstSelectableDate={firstSelectableDate}
                      lastSelectableDate={lastSelectableDate}
                      locale={this.props.intl.locale}
                      onTimeperiodChange={this.onPeriodChange}
                      style={{
                        marginLeft: theme.settings.spacing.large,
                        marginRight: theme.settings.spacing.large,
                        marginTop: theme.settings.spacing.large,
                      }}
                      timeperiod={{ toDate: values[1], fromDate: values[0] }}
                    />
                    <Flex
                      flexDirection="row-reverse"
                      justifyContent="space-between"
                      {...css({ margin: theme.settings.spacing.large })}
                    >
                      <Button flush onClick={toggleOpen} size="small" variant="primary">
                        <FormattedMessage {...messages.ok} />
                      </Button>
                      {onClear && (
                        <Button flush onClick={onClear} size="small" variant="clear">
                          <FormattedMessage {...messages.clear} />
                        </Button>
                      )}
                      {onReset && (
                        <Button flush onClick={onReset} size="small" variant="clear">
                          <FormattedMessage {...messages.reset} />
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                  {(showPredefinedRanges || predefinedRanges) && (
                    <div
                      {...css({
                        maxHeight: '460px',
                        overflowY: 'scroll',
                        borderLeft: theme.settings.borders.regular,
                        background: theme.colors.backgrounds.content,
                      })}
                    >
                      <DaterangeDropdownRanges
                        flush
                        onClick={this.onPeriodChange}
                        predefinedRanges={predefinedRanges}
                      />
                    </div>
                  )}
                </Flex>
              )}
              renderToggle={({ toggleOpen, ref }) => (
                <Button
                  direction="down"
                  flush
                  innerRef={ref}
                  multiChoice
                  onClick={() => this.onDone(toggleOpen)}
                  size={buttonSize}
                  style={{ display: 'inline-flex' }}
                  variant={buttonVariant}
                >
                  {this.getLabel()}
                </Button>
              )}
            />
          </div>
        )
      }
    }
  )
)
